import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearch } from '../../contexts/SearchContext';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useGlobalSearchShortcut } from '../../hooks/useGlobalSearchShortcut';
import { focusAppSearchInput } from './focusAppSearchInput';
import { useGsSearchGroups } from './gsSearchGroups';
import { useAppSearchResults } from './appSearchSources';
import { useAppSearchKeyboardNav } from './useAppSearchKeyboardNav';
import { AppSearchResults } from './AppSearchResults';
import {
  documentsFallbackUrl,
  resolveJumpHref,
  scrollGsNodeIntoView,
} from './appSearchJump';
import type { AppSearchJumpTarget, AppSearchResult } from './appSearchTypes';
import './AppSearchController.css';

/** Editor (canvas) route — see appSearchJump.GS_EDITOR_ROUTE. */
const GS_ROUTE = '/apps/gutschrift-check';
/**
 * How long (ms) to keep retrying the GS node pan while the editor page
 * fetches facts/events/lines and mounts the React-Flow canvas after a
 * cold or cross-month navigation. A cold load fans out several queries,
 * so this is a generous wall-clock budget rather than a tiny frame count.
 */
const GS_JUMP_BUDGET_MS = 8000;

/**
 * Perform the in-app jump for a selected result.
 *
 * - ``route`` targets navigate directly (the /documents fallback path).
 * - ``gs-node`` targets navigate to the gutschrift-check EDITOR (switching
 *   month if needed) and then pan the React-Flow node into view, retrying
 *   across frames until the canvas finishes loading. The retry is bounded
 *   by a wall-clock budget (not a fixed frame count) so a cold/cross-month
 *   load that takes a few seconds still lands the jump. Only after the
 *   budget elapses without the node appearing does it fall back to the
 *   /documents search, so a selection is never a dead end.
 */
export function performJump(
  target: AppSearchJumpTarget,
  deps: {
    navigate: (to: string) => void;
    scrollGsNode?: (groupId: string) => boolean;
    requestFrame?: (cb: () => void) => void;
    now?: () => number;
    fallbackQuery?: string;
    budgetMs?: number;
  },
): void {
  const {
    navigate,
    scrollGsNode = scrollGsNodeIntoView,
    requestFrame = (cb) => window.requestAnimationFrame(cb),
    now = () => Date.now(),
    fallbackQuery = '',
    budgetMs = GS_JUMP_BUDGET_MS,
  } = deps;

  if (target.type === 'route') {
    navigate(target.to);
    return;
  }

  navigate(resolveJumpHref(target));
  const deadline = now() + budgetMs;
  const attempt = () => {
    if (scrollGsNode(target.groupId)) return;
    if (now() >= deadline) {
      // Node never rendered within the budget — fall back to the
      // documents search so the user still lands somewhere relevant.
      navigate(documentsFallbackUrl(fallbackQuery));
      return;
    }
    requestFrame(attempt);
  };
  requestFrame(attempt);
}

/**
 * #1392 — app-wide (Cmd/Ctrl+F) search controller.
 *
 * Mounted once inside the protected layout. Owns the keyboard shortcut,
 * the open/closed state of the results dropdown, the tenant-wide result
 * composition, and the in-app jump. Fully isolated from the
 * gutschrift-check canvas chokepoint files.
 */
export const AppSearchController: React.FC = () => {
  const { searchQuery, clearSearch } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Warm the all-month GS facts whenever the global search is open OR the
  // user is already on a gutschrift-check route. This makes GS matches
  // tenant-wide in app-search from a fresh session (codex round-2 P2):
  // previously the facts were only fetched on the GS route, so Cmd/Ctrl+F
  // returned document hits only until the user had visited GS-Check once.
  const onGsRoute = location.pathname.startsWith(GS_ROUTE);
  // #1392 codex r3 P1 — gate GS data behind the SAME flag as the GS route's
  // FeatureGate (apps.gutschrift_check). Without it, Cmd/Ctrl+F must neither
  // fetch nor surface GS-Check agreements/doc-refs (feature-gated data) nor
  // offer a GS jump the user can't open.
  const hasGsApp = useFeatureFlag('apps.gutschrift_check');
  const gsGroups = useGsSearchGroups(hasGsApp && (open || onGsRoute));

  const { results, isLoading } = useAppSearchResults({
    query: searchQuery,
    gsGroups,
    enabled: open,
  });

  // Cmd/Ctrl+F → focus the header search box and open the dropdown.
  useGlobalSearchShortcut(
    useCallback(() => {
      focusAppSearchInput();
      setOpen(true);
    }, []),
  );

  // Close the dropdown on route change (a jump or a manual nav).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleSelect = useCallback(
    (result: AppSearchResult) => {
      setOpen(false);
      performJump(result.jump, {
        navigate: (to) => navigate(to),
        fallbackQuery: searchQuery,
      });
    },
    [navigate, searchQuery],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    clearSearch();
  }, [clearSearch]);

  // Document-level keyboard nav: focus stays in the header search input
  // after Cmd/Ctrl+F, so Arrow/Enter/Escape are handled here rather than
  // on the dropdown element (which never receives them). See the hook.
  const { activeIndex, setActiveIndex } = useAppSearchKeyboardNav({
    open,
    results,
    onSelect: handleSelect,
    onClose: handleClose,
  });

  if (!open) return null;

  return (
    <div className="app-search-controller">
      <AppSearchResults
        open={open}
        query={searchQuery}
        results={results}
        isLoading={isLoading}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        onSelect={handleSelect}
      />
    </div>
  );
};

export default AppSearchController;
